import { FolderIcon, MoreVerticalIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Image } from '@components/image';
import { useConfirmation, useModal } from '@components/modal';
import { Typography } from '@components/typography';
import {
  GetVolumeDataQuery,
  GetVolumeDetailsVolumeDataQuery,
  useDeleteVolumeDataMutation,
} from '@generated/graphql';
import { saveFile } from '@helper-functions/save-file';
import { formatTime } from '@helper-functions/time';
import { Menu, MenuItem, Stack, TableCell, useTheme } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { Dispatch, SetStateAction, memo, useState } from 'react';
import { Preview3DModal } from '../Preview3DModal';
import { RenameItemModal } from '../RenameItemModal';
import { ThumbnailUploadModal } from '../ThumbnailUploadModal';

const supportedImageExtensions = ['png', 'gif', 'jpeg', 'jpg'];

interface ListViewItemProps {
  volumeId: string;
  volume: GetVolumeDataQuery['getVolumeData']['keys'][0] & { type?: string };
  volumeMetadata: GetVolumeDetailsVolumeDataQuery['getVolumes'][0];
  setDirectory: Dispatch<SetStateAction<string[]>>;
  directory: string[];
  refetchVolumes: () => Promise<unknown>;
}

export const ListViewItem = memo(
  ({
    volume,
    volumeMetadata,
    volumeId,
    setDirectory,
    directory,
    refetchVolumes,
  }: ListViewItemProps) => {
    const { showConfirmation } = useConfirmation();
    const { useAsyncNotification, addNotification, removeNotification } = useNotifications();
    const { palette } = useTheme();
    const [anchorEl, setAnchorEl] = useState<Element>(null);
    const open = Boolean(anchorEl);
    const handleMenuClick = (event: MouseEvent) => setAnchorEl(event.currentTarget as Element);
    const handleMenuClose = () => setAnchorEl(null);

    const [deleteVolumeData] = useDeleteVolumeDataMutation();
    const { showModal: showRenameModal } = useModal({
      component: RenameItemModal,
      componentProps: { name: volume.key, volumeId, refetchVolumes, directory },
      modalProps: { title: 'Rename item' },
    });
    const { showModal: showVolumeThumbnailUploadModal } = useModal({
      component: ThumbnailUploadModal,
      componentProps: {
        name: volume.key,
        volumeId,
        directory,
        refetchVolumeData: refetchVolumes,
      },
      modalProps: { title: 'Upload thumbnail' },
    });

    const handleShowRenameModal = () => {
      handleMenuClose();
      showRenameModal({
        name: volume?.key,
      });
    };
    const handleShowThumbnailUploadModal = () => {
      handleMenuClose();
      showVolumeThumbnailUploadModal({
        name: volume?.key,
      });
    };
    const handleDownload = async () => {
      handleMenuClose();
      const key = `${Date.now() * 1000}.${Math.random() * 10000}`;
      try {
        addNotification({ text: 'Loading...', type: 'loading', key, duration: 100000 });
        const file = await fetch(volume?.url);
        const blob = await file.blob();
        const url = window.URL.createObjectURL(blob);
        saveFile({
          url,
          name: `${volume?.key}`,
        });
        removeNotification(key);
        addNotification({
          text: 'Starting download...',
          type: 'success',
          duration: 6000,
        });
      } catch (e) {
        removeNotification(key);
      }
    };
    const handleShowDeleteModal = async () => {
      handleMenuClose();
      await showConfirmation({
        onAffirm: async () => {
          await useAsyncNotification('Successfully deleted item.', async () => {
            await deleteVolumeData({
              variables: { volumeId, keys: [[...directory, volume?.key].join('')] },
            });
            await refetchVolumes();
          })();
        },
        message: `Are you sure you want to delete ${[...directory, volume?.key].join('')}?`,
      });
    };

    const { showModal: showVolume3DPreviewModal } = useModal({
      component: Preview3DModal,
      componentProps: {
        name: volume.key,
        previewUrl: volume.previewUrl,
        imageUrl: volume.url,
        options: (
          <>
            <MenuItem onClick={handleShowRenameModal}>
              <Typography variant="caption2">Rename</Typography>
            </MenuItem>
            <MenuItem onClick={handleShowThumbnailUploadModal}>
              <Typography variant="caption2">Thumbnail</Typography>
            </MenuItem>
            <MenuItem onClick={handleShowDeleteModal}>
              <Typography variant="caption2">Delete</Typography>
            </MenuItem>
          </>
        ),
      },
      modalProps: { disableAutoFocus: true, disablePortal: true },
    });

    const handleVolumeNameClick =
      volume?.type === 'folder'
        ? () => setDirectory((fh) => [...fh, volume?.key])
        : volume?.previewUrl
          ? showVolume3DPreviewModal
          : supportedImageExtensions.includes(volume.key.split('.').pop())
            ? showVolume3DPreviewModal
            : undefined;

    return (
      <>
        <TableCell sx={{ cursor: volume?.type === 'folder' ? 'pointer' : 'default' }}>
          {volume?.type === 'folder' ? (
            <Stack
              justifyContent="center"
              alignItems="center"
              sx={{ height: '48px', width: '48px', backgroundColor: palette.background.default }}
            >
              <FolderIcon size="40%" />
            </Stack>
          ) : (
            <Image url={volume?.thumbnailUrl} sx={{ height: '48px', width: '48px' }} />
          )}
        </TableCell>
        <TableCell sx={{ cursor: volume?.type === 'folder' ? 'pointer' : 'default' }}>
          <Typography handleOverFlow variant="body2" onClick={handleVolumeNameClick}>
            {volume?.key || '-'}
          </Typography>
        </TableCell>
        <TableCell sx={{ cursor: volume?.type === 'folder' ? 'pointer' : 'default' }}>
          <Typography variant="body2">-</Typography>
        </TableCell>
        <TableCell sx={{ cursor: volume?.type === 'folder' ? 'pointer' : 'default' }}>
          <Typography variant="body2">
            {volume?.updatedAt ? formatTime(volume?.updatedAt) : '-'}
          </Typography>
        </TableCell>
        <TableCell sx={{ cursor: volume?.type === 'folder' ? 'pointer' : 'default' }}>
          <Typography variant="body2">
            {volume?.size > 0 ? `${Number(Number(volume?.size) * 0.000000001).toFixed(3)} GB` : '-'}
          </Typography>
        </TableCell>
        <TableCell sx={{ cursor: volume?.type === 'folder' ? 'pointer' : 'default' }}>
          {volumeMetadata.permission !== 'view' && (
            <>
              <IconButton
                onClick={handleMenuClick as () => void}
                Icon={MoreVerticalIcon}
                size={20}
              />
              <Menu
                open={open}
                anchorEl={anchorEl}
                onClose={handleMenuClose}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {volume?.type !== 'folder' && (
                  <MenuItem onClick={handleDownload}>
                    <Typography variant="caption2">Download</Typography>
                  </MenuItem>
                )}
                {volumeMetadata.permission === 'write' && (
                  <>
                    <MenuItem onClick={handleShowRenameModal}>
                      <Typography variant="caption2">Rename</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleShowThumbnailUploadModal}>
                      <Typography variant="caption2">Thumbnail</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleShowDeleteModal}>
                      <Typography variant="caption2">Delete</Typography>
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          )}
        </TableCell>
      </>
    );
  },
);
